import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Container,
  TextField,
  Button,
  Typography,
  Alert,
  Paper,
  Link as MuiLink,
  IconButton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Link, useHistory } from 'react-router-dom';
import { IdentityContext, UserManagerContext } from '@react-beets/identity';
import identityService from '../../services/identityService';
import { ROUTES } from '../../constants';
import '../IdentityLogin/styles.css';

function ManageProfile() {
  const { identity: user, login } = useContext(IdentityContext);
  const { userManager } = useContext(UserManagerContext);
  const history = useHistory();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validationErrors, setValidationErrors] = useState({});

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    try {
      setLoading(true);
      const profile = await identityService.getProfile(user?.access_token);
      setFormData({
        firstName: profile.firstName || '',
        lastName: profile.lastName || '',
        email: profile.email || '',
      });
    } catch (err) {
      setError('Failed to load profile');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors({
        ...validationErrors,
        [name]: '',
      });
    }
  };

  const validateForm = () => {
    const errors = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (!validateForm()) {
      return;
    }

    setSaving(true);

    try {
      await identityService.updateProfile(
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
        },
        user?.access_token
      );

      setSuccess('Profile updated successfully');

      // Hybrid approach: Update context immediately + refresh token in background
      if (user && login) {
        // Step 1: Immediate context update for instant UI feedback
        const updatedUser = {
          ...user,
          profile: {
            ...user.profile,
            given_name: formData.firstName,
            family_name: formData.lastName,
          },
        };
        login(updatedUser);

        // Step 2: Silent token refresh in background to sync with backend
        if (userManager) {
          try {
            const refreshedUser = await userManager.signinSilent();
            if (refreshedUser) {
              login(refreshedUser);
            }
          } catch (refreshError) {
            console.error('Silent token refresh failed:', refreshError);
            // Not a critical error - the context was already updated with new values
          }
        }
      }
    } catch (err) {
      if (err.response?.data?.message) {
        setError(err.response.data.message);
      } else {
        setError('An error occurred while updating your profile');
      }
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="identity-page">
        <Container maxWidth="sm">
          <Box sx={{ marginTop: 8, textAlign: 'center' }}>
            <Typography>Loading...</Typography>
          </Box>
        </Container>
      </div>
    );
  }

  return (
    <div className="identity-page">
      <Container maxWidth="sm">
        <Box
          sx={{
            marginTop: 8,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          <Paper elevation={3} sx={{ padding: 4, width: '100%', position: 'relative' }}>
            <IconButton
              onClick={() => history.push(ROUTES.APP_HOME)}
              sx={{
                position: 'absolute',
                top: 16,
                left: 16,
                color: '#8B3040',
                '&:hover': {
                  backgroundColor: 'rgba(139, 48, 64, 0.04)',
                },
              }}
              aria-label="go to home"
            >
              <ArrowBackIcon />
            </IconButton>

            <Typography
              component="h2"
              variant="h5"
              align="center"
              sx={{ marginBottom: 3 }}
            >
              My Profile
            </Typography>

            {error && (
              <Alert severity="error" sx={{ marginBottom: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ marginBottom: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                margin="normal"
                required
                fullWidth
                id="firstName"
                label="First Name"
                name="firstName"
                autoComplete="given-name"
                value={formData.firstName}
                onChange={handleChange}
                disabled={saving}
                error={!!validationErrors.firstName}
                helperText={validationErrors.firstName}
              />

              <TextField
                margin="normal"
                required
                fullWidth
                id="lastName"
                label="Last Name"
                name="lastName"
                autoComplete="family-name"
                value={formData.lastName}
                onChange={handleChange}
                disabled={saving}
                error={!!validationErrors.lastName}
                helperText={validationErrors.lastName}
              />

              <TextField
                margin="normal"
                fullWidth
                id="email"
                label="Email Address"
                name="email"
                autoComplete="email"
                value={formData.email}
                disabled
                helperText="Email cannot be changed"
              />

              <Box sx={{ display: 'flex', gap: 2, mt: 3, mb: 2 }}>
                <Button
                  type="submit"
                  fullWidth
                  variant="contained"
                  sx={{
                    backgroundColor: '#8B3040',
                    '&:hover': {
                      backgroundColor: '#6d2533',
                    },
                  }}
                  disabled={saving}
                >
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>

                <Button
                  type="button"
                  fullWidth
                  variant="outlined"
                  sx={{
                    borderColor: '#8B3040',
                    color: '#8B3040',
                    '&:hover': {
                      borderColor: '#6d2533',
                      backgroundColor: 'rgba(139, 48, 64, 0.04)',
                    },
                  }}
                  disabled={saving}
                  onClick={() => {
                    setError('');
                    setSuccess('');
                    setValidationErrors({});
                    loadProfile();
                  }}
                >
                  Reset
                </Button>
              </Box>

              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" sx={{ marginBottom: 1 }}>
                  <MuiLink
                    component={Link}
                    to={ROUTES.MANAGE_CHANGE_PASSWORD}
                    sx={{ color: '#8B3040', fontWeight: 'bold' }}
                  >
                    Change Password
                  </MuiLink>
                </Typography>
                <Typography variant="body2">
                  <MuiLink
                    component={Link}
                    to={ROUTES.MANAGE_PERSONAL_DATA}
                    sx={{ color: '#8B3040', fontWeight: 'bold' }}
                  >
                    Download or Delete Your Data
                  </MuiLink>
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Box>
      </Container>
    </div>
  );
}

export default ManageProfile;
